import { Helmet } from "react-helmet-async";
import { useI18n } from "../lib/i18n";
import { Link } from "react-router-dom";
import { chf } from "../lib/format";
import { Loader2, PackageOpen, AlertTriangle, Star } from "lucide-react";

export const BRAND = "ANEL Furniture";
export const Logo = ({ className = "", light = false, size = "h-12 lg:h-14" }) => (
  <img src={light ? "/logo-light.png" : "/logo.png"} alt={`${BRAND} – Mobileri Anel`} width="517" height="455" className={`${size} w-auto ${className}`} data-testid="brand-logo" />
);

export const Seo = ({ title, description, image, canonical, jsonLd, noindex }) => {
  const { lang } = useI18n();
  const url = canonical || window.location.origin + window.location.pathname;
  return (
    <Helmet>
      <html lang={`${lang}-CH`} />
      <title>{title ? `${title} | ${BRAND}` : `${BRAND} – Möbel online Schweiz`}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={lang === "de" ? url : `${url}?lang=${lang}`} />
      {["de", "fr", "it", "en", "sq"].map((l) => <link key={l} rel="alternate" hrefLang={`${l}-CH`} href={`${url}?lang=${l}`} />)}
      <meta property="og:title" content={title || BRAND} />
      {description && <meta property="og:description" content={description} />}
      {image && <meta property="og:image" content={image} />}
      <meta property="og:type" content="website" />
      {noindex && <meta name="robots" content="noindex,follow" />}
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export const Loading = ({ label }) => {
  const { t } = useI18n();
  return <div className="flex items-center gap-2 py-16 justify-center text-sm text-[color:var(--hw-muted)]" data-testid="loading-state"><Loader2 className="animate-spin" size={18} />{label || t("loading")}</div>;
};

export const Empty = ({ title, text, action }) => (
  <div className="py-16 text-center" data-testid="empty-state">
    <PackageOpen className="mx-auto mb-4 text-[color:var(--hw-muted)]" size={36} strokeWidth={1.3} />
    <h3 className="text-2xl mb-2">{title}</h3>
    {text && <p className="text-sm text-[color:var(--hw-muted)] mb-6">{text}</p>}
    {action}
  </div>
);

export const ErrorState = ({ onRetry }) => {
  const { t } = useI18n();
  return (
    <div className="py-16 text-center" data-testid="error-state">
      <AlertTriangle className="mx-auto mb-3 text-[color:var(--hw-sale)]" size={30} />
      <p className="text-sm mb-4">{t("error")}</p>
      {onRetry && <button className="btn-outline" onClick={onRetry} data-testid="error-retry-button">{t("retry")}</button>}
    </div>
  );
};

export const Stars = ({ value, count, size = 13 }) => {
  const { t } = useI18n();
  if (!count) return <span className="text-xs text-[color:var(--hw-muted)]">{t("no_reviews")}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-xs" aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} className={i <= Math.round(value) ? "fill-[color:var(--hw-ink)] text-[color:var(--hw-ink)]" : "text-[color:var(--hw-line)]"} />)}
      <span className="ml-1 text-[color:var(--hw-muted)]">{value} ({count})</span>
    </span>
  );
};

export const Price = ({ price, compare, from, size = "base", testid }) => {
  const { t } = useI18n();
  const sale = compare && compare > price;
  const pct = sale ? Math.round(((compare - price) * 100) / compare) : 0;
  const cls = size === "lg" ? "text-3xl" : "text-base";
  return (
    <div className="flex items-baseline flex-wrap gap-x-2" data-testid={testid}>
      <span className={`${cls} font-semibold ${sale ? "text-[color:var(--hw-sale)]" : ""}`}>{from && <span className="text-xs font-normal mr-1">{t("from")}</span>}{chf(price)}</span>
      {sale && <span className="text-sm line-through text-[color:var(--hw-muted)]">{chf(compare)}</span>}
      {sale && <span className="text-xs font-semibold text-[color:var(--hw-sale)]">−{pct}%</span>}
    </div>
  );
};

export const SectionHead = ({ eyebrow, title, link, linkLabel, testid }) => (
  <div className="flex items-end justify-between gap-4 mb-8" data-testid={testid}>
    <div>{eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}<h2 className="h2">{title}</h2></div>
    {link && <Link to={link} className="text-sm font-semibold link-u whitespace-nowrap">{linkLabel}</Link>}
  </div>
);
